import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CheckCircle2, Clock, Crown, Info, UserPlus, Users } from "lucide-react";
import {
  computeConfirmation,
  computeLeadTimeHours,
  computeLoadCurve,
  computeOnBooksPax,
  computeOriginMix,
  computePacingPerHour,
  computeTagCounts,
  formatHourLabel,
  formatPct,
} from "@/lib/day-metrics";
import { useAppStore, useTodayReservations } from "@/store/store";
import { useKitchenCapacityWindows } from "@/hooks/use-kitchen-capacity";
import { dayjs, parseTime } from "@/utils/date";

const ORIGIN_LABELS: Record<string, string> = {
  seimseim: "Staff",
  walkin: "Walk-in",
  google: "Google",
  whatsapp: "WhatsApp",
  web: "Web",
  phone: "Teléfono",
  agent: "Agente IA",
};

function Kpi({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-white p-3 flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-text-secondary uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold leading-tight">{value}</div>
      {hint && <div className="text-[11px] text-text-secondary">{hint}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-white p-4 flex flex-col gap-3 min-w-0">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </div>
  );
}

/** Kitchen-cap value for a given local day-of-week + hour, derived
 *  from the windows endpoint. Returns ``null`` when no window covers
 *  the slot — same semantics as the backend's ``kitchen_capacity_at``. */
function kitchenCapForHour(
  windows: Array<{ day_of_week: number; start_time: string; end_time: string; max_pax: number }>,
  weekday: number,
  hour: number,
): number | null {
  const minute = hour * 60;
  const caps: number[] = [];
  for (const w of windows) {
    if (w.day_of_week !== weekday) continue;
    const [sh, sm] = w.start_time.split(":").map(Number);
    const [eh, em] = w.end_time.split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    if (minute >= start && minute < end) caps.push(w.max_pax);
  }
  return caps.length === 0 ? null : Math.min(...caps);
}

export default function OnTheBooksReport() {
  const reservations = useTodayReservations();
  const selectedDate = useAppStore((s) => s.selectedDate);
  const { data: kitchenWindows } = useKitchenCapacityWindows();

  const active = useMemo(
    () => reservations.filter((r) => r.status !== "cancelada"),
    [reservations],
  );

  const onBooksPax = computeOnBooksPax(active);
  const confirmation = computeConfirmation(active);
  const vipCount = active.filter((r) => r.isVip).length;
  const walkinAlready = active.filter((r) => r.origin === "walkin").length;
  const leadTime = computeLeadTimeHours(active);

  const tables = useAppStore((s) => s.tables);
  const pacing = useMemo(() => computePacingPerHour(active), [active]);
  const loadCurve = useMemo(() => computeLoadCurve(active, tables), [active, tables]);
  const origin = useMemo(() => computeOriginMix(active), [active]);
  const tags = useMemo(() => computeTagCounts(active), [active]);
  const dietaryTags = useMemo(() => tags.filter((t) => t.isDietary), [tags]);
  const partyTags = useMemo(() => tags.filter((t) => !t.isDietary), [tags]);

  const weekday = useMemo(() => {
    // Date string is local-day already; use dayjs to find weekday with
    // Django's convention (Mon=0). dayjs.day() returns Sun=0, so shift.
    const d = dayjs(selectedDate).day();
    return (d + 6) % 7;
  }, [selectedDate]);

  const pacingChartData = useMemo(() => {
    return pacing.map((row) => ({
      hour: row.hour,
      label: formatHourLabel(row.hour),
      pax: row.pax,
      cap: kitchenCapForHour(kitchenWindows ?? [], weekday, row.hour),
    }));
  }, [pacing, kitchenWindows, weekday]);

  const vips = useMemo(
    () =>
      active
        .filter((r) => r.isVip || r.internalNotes)
        .toSorted((a, b) => parseTime(a.time).valueOf() - parseTime(b.time).valueOf()),
    [active],
  );

  if (active.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-text-secondary gap-2 py-12">
        <Info size={32} className="opacity-40" />
        <p className="text-sm">Aún no hay reservas en libros para este día.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6">
      <div className="mb-4">
        <h2 className="text-base font-bold">En libros (pre-servicio)</h2>
        <p className="text-xs text-text-secondary">
          Lo que está reservado para este día. Útil para preparar el servicio.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Kpi icon={<Users size={14} />} label="Reservas" value={active.length} />
        <Kpi icon={<Users size={14} />} label="Covers proyectados" value={onBooksPax} />
        <Kpi
          icon={<CheckCircle2 size={14} />}
          label="Confirmadas"
          value={confirmation.confirmed}
          hint={`${confirmation.pending} sin confirmar`}
        />
        <Kpi icon={<Crown size={14} />} label="VIPs" value={vipCount} />
        <Kpi
          icon={<Clock size={14} />}
          label="Antelación promedio"
          value={leadTime > 0 ? `${leadTime}h` : "—"}
          hint="entre creación y reserva"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lg:col-span-2">
          <Card title="Carga proyectada (cada 15 min)">
            {loadCurve.length === 0 || tables.length === 0 ? (
              <p className="text-xs text-text-secondary">
                Sin datos para proyectar la carga
                {tables.length === 0 ? " (no hay mesas configuradas)" : ""}.
              </p>
            ) : (
              <div style={{ height: Math.max(220, loadCurve.length * 22) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={loadCurve}
                    layout="vertical"
                    margin={{ top: 4, right: 56, bottom: 4, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 1]}
                      tickFormatter={(v) => formatPct(v)}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis
                      dataKey="slot"
                      type="category"
                      tick={{ fontSize: 11 }}
                      width={60}
                      interval={0}
                    />
                    <Tooltip
                      formatter={(
                        value: number,
                        _name,
                        item: { payload?: { mesas?: number; pax?: number } },
                      ) => {
                        const row = item.payload;
                        return [
                          `${formatPct(value)} · ${row?.mesas ?? 0} mesas · ${row?.pax ?? 0} pax`,
                          "Carga",
                        ];
                      }}
                    />
                    <Bar
                      dataKey="pct"
                      name="Carga"
                      fill="#0ea5e9"
                      radius={[0, 4, 4, 0]}
                      label={{
                        position: "right",
                        fontSize: 11,
                        fill: "#475569",
                        formatter: (v: number) => formatPct(v),
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <p className="text-[11px] text-text-secondary">
              Cada fila muestra cuántas mesas y pax estarán ocupadas en ese intervalo, calculado por
              el solapamiento de las reservas en libros. % es sobre la capacidad total de asientos
              del restaurante.
            </p>
          </Card>
        </div>
        <Card title="Pacing por hora vs tope de cocina">
          {pacingChartData.length === 0 ? (
            <p className="text-xs text-text-secondary">Sin datos por hora.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pacingChartData} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="pax" name="Pax en libros" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  {pacingChartData.some((d) => d.cap !== null) && (
                    <ReferenceLine
                      y={Math.max(...pacingChartData.map((d) => d.cap ?? 0))}
                      stroke="#dc2626"
                      strokeDasharray="4 4"
                      label={{
                        value: "Tope cocina",
                        position: "insideTopRight",
                        fontSize: 11,
                        fill: "#dc2626",
                      }}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Mix por canal">
          {origin.length === 0 ? (
            <p className="text-xs text-text-secondary">Sin reservas para mostrar.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={origin.map((o) => ({
                    label: ORIGIN_LABELS[o.origin] ?? o.origin,
                    count: o.count,
                  }))}
                  layout="vertical"
                  margin={{ top: 4, right: 12, bottom: 4, left: 24 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={70} />
                  <Tooltip />
                  <Bar dataKey="count" name="Reservas" fill="#10b981" radius={[0, 4, 4, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Restricciones a preparar">
          {dietaryTags.length === 0 ? (
            <p className="text-xs text-text-secondary">Sin restricciones registradas.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {dietaryTags.map((t) => (
                <li
                  key={t.tag}
                  className="text-xs px-2 py-1 rounded-md bg-surface-alt text-text-secondary"
                >
                  <span className="font-medium text-foreground">{t.label}</span> · {t.count}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Bebés y mascotas no son restricciones de cocina: van en su
            propia tarjeta para que el chef no lea "Con mascota" como una
            dieta a preparar. */}
        {partyTags.length > 0 && (
          <Card title="En la mesa">
            <ul className="flex flex-wrap gap-2">
              {partyTags.map((t) => (
                <li
                  key={t.tag}
                  className="text-xs px-2 py-1 rounded-md bg-surface-alt text-text-secondary"
                >
                  <span className="font-medium text-foreground">{t.label}</span> · {t.count}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card title={`Atención especial (${vips.length})`}>
          {vips.length === 0 ? (
            <p className="text-xs text-text-secondary">Sin VIPs ni notas internas.</p>
          ) : (
            <ul className="flex flex-col gap-1.5 text-sm">
              {vips.map((r) => (
                <li
                  key={r.id}
                  className="flex items-start justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {r.isVip && <span className="text-orange-500 mr-1">★</span>}
                      {r.guestName}
                    </div>
                    {r.internalNotes && (
                      <div className="text-[11px] text-text-secondary truncate">
                        {r.internalNotes}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary shrink-0 flex items-center gap-1">
                    <UserPlus size={12} /> {r.pax}p · {r.time}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {walkinAlready > 0 && (
        <p className="mt-4 text-[11px] text-text-secondary">
          Hoy ya entraron {walkinAlready} walk-ins (no cuentan al pacing proyectado).
        </p>
      )}
    </div>
  );
}
